// src/theme.js
// Wrap your app in App.js or index.js:
//
//   import { ThemeProvider } from '@mui/material/styles';
//   import CssBaseline from '@mui/material/CssBaseline';
//   import theme from './theme';
//
//   root.render(
//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       <App />
//     </ThemeProvider>
//   );

import { createTheme } from '@mui/material/styles';

export const BRAND = {
  900: '#1A0E08',
  800: '#2C1810',   // primary — replaces MUI cobalt #1976D2
  700: '#3D2318',
  600: '#5C3420',
  400: '#8B5A3A',
  200: '#D4B09A',
  100: '#F0E4DA',
  50:  '#FAF5F1',
  gold: '#8B6914',
  goldLight: '#FDF3DC',
};

// Horse type badge colours — imported by HorseTypeBadge.jsx
export const TYPE_COLORS = {
  Mare:     { bg: '#EDE7F6', color: '#4527A0', border: '#7E57C2' },
  Stallion: { bg: '#E3F2FD', color: '#1565C0', border: '#42A5F5' },
  Colt:     { bg: '#E8F5E9', color: '#2E7D32', border: '#66BB6A' },
  Filly:    { bg: '#FCE4EC', color: '#AD1457', border: '#F48FB1' },
  Gelding:  { bg: '#FFF3E0', color: '#E65100', border: '#FFB74D' },
};

const theme = createTheme({
  palette: {
    primary:    { main: BRAND[800], light: BRAND[600], dark: BRAND[900], contrastText: '#fff' },
    secondary:  { main: BRAND.gold, light: BRAND.goldLight, contrastText: '#fff' },
    background: { default: '#F5F2EE', paper: '#FFFFFF' },
    text:       { primary: '#1A0E08', secondary: '#5C4030' },
    divider:    '#E8DDD5',
  },

  typography: {
    // System face: it ships optical sizing and per-size tracking, and costs no download.
    // ("Inter" was named here but never loaded, so Helvetica/Arial was what rendered.)
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    // Tracking tightens as type grows: MUI's defaults give h4 (34px page titles) and h6
    // *positive* letter-spacing, which reads loose at display sizes. Body text keeps 0.
    h4: { letterSpacing: '-0.01em', lineHeight: 1.2 },
    h5: { fontWeight: 700, fontSize: '1.2rem' },
    h6: { fontWeight: 700, letterSpacing: 0 },
  },

  shape: { borderRadius: 8 },

  components: {
    // Reduced motion: collapse every transition (dialogs, drawers, collapses, tab fades)
    // to its end state. Animations are deliberately left alone — the only ones in the app
    // are MUI's progress spinners and ripple, which are status/press feedback, and capping
    // their iteration count would freeze a spinner mid-load.
    MuiCssBaseline: {
      styleOverrides: {
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            transitionDuration: '0.01ms !important',
            transitionDelay: '0ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    // Translucent bar: pages that scroll the window (Dashboard, detail pages, Reports)
    // pass under it, so it reads as a layer above the content rather than a strip cut out
    // of it. Solid wherever blur is unsupported or the user asked for less transparency
    // or more contrast — translucency must never cost legibility.
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: BRAND[800],
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          '@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
            backgroundColor: 'rgba(44, 24, 16, 0.9)', // BRAND[800]; at 0.82 it went muddy taupe over the light page
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          },
          '@media (prefers-reduced-transparency: reduce), (prefers-contrast: more)': {
            backgroundColor: `${BRAND[800]} !important`,
            backdropFilter: 'none !important',
            WebkitBackdropFilter: 'none !important',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#FDFAF7', borderRight: `1px solid ${BRAND[100]}` },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6, margin: '1px 8px', paddingLeft: 12,
          '&.Mui-selected': {
            backgroundColor: BRAND[50],
            borderLeft: `3px solid ${BRAND[800]}`,
            paddingLeft: 9,
            color: BRAND[800],
            '& .MuiListItemIcon-root': { color: BRAND[800] },
            '&:hover': { backgroundColor: BRAND[100] },
          },
          '&:hover': { backgroundColor: '#F0E8E0' },
        },
      },
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 36, color: '#7A6050' } } },
    MuiListItemText: { styleOverrides: { primary: { fontSize: '0.875rem', fontWeight: 500 } } },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 8, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        containedPrimary: { backgroundColor: BRAND[800], '&:hover': { backgroundColor: BRAND[700] } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { minWidth: 36, minHeight: 36 } } },
    // Shadow-only card look, no border line.
    MuiCard:  { styleOverrides: { root: ({ theme: t }) => ({ boxShadow: t.shadows[3], border: 'none' }) } },
    MuiPaper: { styleOverrides: { elevation4: { boxShadow: '0 1px 4px rgba(0,0,0,0.08)' } } },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: BRAND[50], fontWeight: 700, fontSize: '0.72rem',
            color: '#5C4030', textTransform: 'uppercase', letterSpacing: '0.05em',
            borderBottom: `2px solid ${BRAND[100]}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: BRAND[50] }, '&:last-child td': { borderBottom: 0 } },
      },
    },
    MuiTableCell: { styleOverrides: { root: { borderColor: BRAND[100], fontSize: '0.875rem', padding: '10px 16px' } } },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', '&.Mui-selected': { color: BRAND[800] } },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: BRAND[800] } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, fontSize: '0.72rem', height: 22, borderRadius: 4 } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          '& fieldset': { borderColor: BRAND[200] },
          '&:hover fieldset': { borderColor: BRAND[600] },
          '&.Mui-focused fieldset': { borderColor: BRAND[800] },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: BRAND[900], fontSize: '0.75rem', borderRadius: 6, padding: '5px 10px' },
        arrow: { color: BRAND[900] },
      },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 12 } } },
  },
});

// Canonical "card" look for plain Paper-as-card usages that can't take the
// blanket MuiCard override above (Paper is also used internally by
// Menu/Select/Dialog). Shadow-only, no border — mirrors MuiCard exactly.
export const cardSx = { boxShadow: theme.shadows[3] };

export default theme;
